import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Alert, Image, Form, Input, Checkbox, theme, Divider } from 'antd';
import { UserOutlined, LockOutlined, KeyOutlined, WindowsOutlined } from '@ant-design/icons';
import { signIn, confirmSignIn, fetchAuthSession, fetchUserAttributes, resetPassword, confirmResetPassword, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import logo from '../../assets/prism-white-logo.png';
import Loader from '../../components/Loader';
import { getAuthPersistence, setAuthPersistence, decodeJwt } from '../../features/auth';

type AuthView = 'login' | 'forgot-request' | 'forgot-confirm' | 'new-password-required';

const Login = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const loggedOut = searchParams.get('loggedOut') === '1';
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [authView, setAuthView] = useState<AuthView>('login');
  const [resetUsername, setResetUsername] = useState('');

  const ssoEnabled = !!process.env.REACT_APP_AUTH_DOMAIN;

  const completeSignIn = async (fallbackUsername: string) => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const idToken = session.tokens?.idToken?.toString();

    if (accessToken) {
      sessionStorage.setItem('access_token', accessToken);
    }
    if (idToken) {
      sessionStorage.setItem('id_token', idToken);
    }

    const accessClaims = accessToken ? (decodeJwt(accessToken) || {}) : {};
    const idClaims = idToken ? (decodeJwt(idToken) || {}) : {};

    // Federated (SSO) sign-ins already carry email/name claims in the ID
    // token from the upstream IdP via Cognito. Calling Cognito's GetUser API
    // (fetchUserAttributes) here too would be a redundant, unwanted Cognito
    // authentication API call for SSO users - only fall back to it for
    // native Cognito (username/password) users, whose ID token may lack
    // custom attributes.
    const isFederatedSignIn = !!idClaims.identities?.length;
    const attributes = isFederatedSignIn ? {} : ((await fetchUserAttributes().catch(() => ({}))) as Record<string, string>);

    const userEmail =
      idClaims.email ||
      idClaims.preferred_username ||
      attributes.email ||
      attributes.preferred_username ||
      fallbackUsername;

    const userFullName =
      idClaims.name ||
      idClaims['custom:fullname'] ||
      idClaims['custom:name'] ||
      accessClaims.fullname ||
      userEmail;

    const isExternal = isFederatedSignIn || accessClaims.username?.includes('_');

    localStorage.setItem(
      'user',
      JSON.stringify({
        email: userEmail,
        fullname: userFullName,
        isExternal,
      })
    );

    const returnUrl = localStorage.getItem('returnUrl') || '/dashboard';
    localStorage.removeItem('returnUrl');
    navigate(returnUrl, { replace: true });
  };

  useEffect(() => {
    form.setFieldsValue({ rememberMe: getAuthPersistence() === 'local' });
  }, [form]);

  // Listen for OAuth redirect callback (Azure AD SSO)
  useEffect(() => {
    if (!ssoEnabled) return;

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // User successfully returned from Azure AD → complete sign-in
        try {
          await completeSignIn('');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to complete SSO sign-in');
          setSsoLoading(false);
        }
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setError('Microsoft SSO sign-in failed. Please try again.');
        setSsoLoading(false);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoEnabled]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        if (loggedOut) {
          setInitializing(false);
          return;
        }
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        if (!accessToken) {
          setInitializing(false);
          return;
        }
        sessionStorage.setItem('access_token', accessToken);
        const returnUrl = localStorage.getItem('returnUrl') || '/dashboard';
        localStorage.removeItem('returnUrl');
        navigate(returnUrl, { replace: true });
      } catch {
        setInitializing(false);
      }
    };

    checkSession();
  }, [form, loggedOut, navigate]);

  const handleSSOLogin = async () => {
    try {
      setError(null);
      setSsoLoading(true);
      await signInWithRedirect({
        provider: { custom: 'AzureAD' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'SSO sign-in failed');
      setSsoLoading(false);
    }
  };

  const handleLogin = async (values: { username: string; password: string; rememberMe?: boolean }) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      const username = values.username.trim();
      const password = values.password;
      setAuthPersistence(values.rememberMe ? 'local' : 'session');

      let response: Awaited<ReturnType<typeof signIn>> = await signIn({
        username,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });

      if (response.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const availableChallenges = response.nextStep.availableChallenges || [];
        const preferredChallenge = availableChallenges.includes('PASSWORD')
          ? 'PASSWORD'
          : availableChallenges.includes('PASSWORD_SRP')
            ? 'PASSWORD_SRP'
            : null;

        if (!preferredChallenge) {
          setError(
            `Supported first factor not available. Available: ${availableChallenges.length ? availableChallenges.join(', ') : 'none'}`
          );
          return;
        }

        response = await confirmSignIn({ challengeResponse: preferredChallenge });
      }

      if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        response = await confirmSignIn({ challengeResponse: password });
      }

      if (response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setResetUsername(username);
        setAuthView('new-password-required');
        setSuccess('Please set a new password to continue.');
        return;
      }

      if (response.nextStep.signInStep !== 'DONE') {
        setError(`Sign in requires additional step: ${response.nextStep.signInStep}`);
        return;
      }

      await completeSignIn(values.username);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to sign in';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRequest = async (values: { username: string }) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      const username = values.username.trim();
      const response = await resetPassword({ username });
      setResetUsername(username);

      if (response.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        const destination = response.nextStep.codeDeliveryDetails?.destination;
        const method = response.nextStep.codeDeliveryDetails?.deliveryMedium;
        setSuccess(
          destination ? `Verification code sent via ${method || 'selected channel'} to ${destination}.` : 'Verification code sent.'
        );
        setAuthView('forgot-confirm');
        form.setFieldsValue({ username });
        return;
      }

      setSuccess('Password reset is already complete. Please sign in with your new password.');
      setAuthView('login');
      form.resetFields(['password']);
      form.setFieldsValue({ username });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to start forgot password flow');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordConfirm = async (values: {
    username?: string;
    confirmationCode: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      const username = (values.username || resetUsername).trim();
      await confirmResetPassword({
        username,
        confirmationCode: values.confirmationCode.trim(),
        newPassword: values.newPassword,
      });
      setSuccess('Password updated successfully. Please sign in with your new password.');
      setAuthView('login');
      setResetUsername(username);
      form.resetFields(['password', 'confirmationCode', 'newPassword', 'confirmNewPassword']);
      form.setFieldsValue({ username });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to confirm new password');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordRequired = async (values: { newPassword: string; confirmNewPassword: string }) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      const response = await confirmSignIn({
        challengeResponse: values.newPassword,
      });

      if (response.nextStep.signInStep !== 'DONE') {
        setError(`Sign in requires additional step: ${response.nextStep.signInStep}`);
        return;
      }

      await completeSignIn(resetUsername || form.getFieldValue('username') || '');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to set new password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (authView === 'login') {
      await handleLogin(values);
      return;
    }
    if (authView === 'forgot-request') {
      await handleForgotPasswordRequest(values);
      return;
    }
    if (authView === 'new-password-required') {
      await handleNewPasswordRequired(values);
      return;
    }
    await handleForgotPasswordConfirm(values);
  };

  const goToForgotPassword = () => {
    setError(null);
    setSuccess(null);
    setAuthView('forgot-request');
    form.resetFields(['password']);
  };

  const goToLogin = () => {
    setError(null);
    setSuccess(null);
    setAuthView('login');
    form.resetFields(['confirmationCode', 'newPassword', 'confirmNewPassword']);
  };

  if (initializing) {
    return <Loader fullScreen />;
  }

  return (
    <div className="w-full h-screen bg-[#3A3A3A] flex flex-col items-center justify-center">
      <Image width={150} src={logo} className="mb-20" preview={false} />
      <Card
        className={`relative overflow-hidden w-[450px] pt-6 rounded-lg shadow-2xl shadow-black/10 ${
          authView === 'forgot-confirm' || authView === 'new-password-required' ? 'h-[520px]' : ssoEnabled ? 'h-[440px]' : 'h-[360px]'
        }`}>
        <div className="absolute -top-5 right-16 h-10 w-10 rounded-full bg-gray-300/60" />
        <div className="absolute -top-12 -right-8 h-24 w-24 rounded-full bg-[#f3d3cc]" />
        <div style={{ minHeight: 56, marginBottom: 16 }}>
          {error ? (
            <Alert
              message="Authentication Error"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 0, padding: '6px 10px', fontSize: 12 }}
            />
          ) : success ? (
            <Alert
              message="Success"
              description={success}
              type="success"
              showIcon
              style={{ marginBottom: 0, padding: '6px 10px', fontSize: 12 }}
            />
          ) : null}
        </div>



        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="username" rules={[{ required: true, message: 'Please enter your username' }]}>
            <Input autoComplete="username" placeholder="Username" prefix={<UserOutlined />} disabled={loading || ssoLoading} />
          </Form.Item>

          {authView === 'login' && (
            <>
              <Form.Item name="password" rules={[{ required: true, message: 'Please enter your password' }]}>
                <Input.Password
                  autoComplete="current-password"
                  placeholder="Password"
                  prefix={<LockOutlined />}
                  disabled={loading || ssoLoading}
                />
              </Form.Item>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -8, marginBottom: 16 }}>
                <Form.Item name="rememberMe" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox disabled={loading || ssoLoading}>Remember me</Checkbox>
                </Form.Item>
                <Button
                  type="link"
                  style={{ padding: 0, color: token.colorPrimary }}
                  onClick={goToForgotPassword}
                  disabled={loading || ssoLoading}>
                  Forgot password?
                </Button>
              </div>
            </>
          )}

          {(authView === 'forgot-confirm' || authView === 'new-password-required') && (
            <>
              {authView === 'forgot-confirm' && (
                <Form.Item name="confirmationCode" rules={[{ required: true, message: 'Please enter verification code' }]}>
                  <Input autoComplete="one-time-code" placeholder="Verification Code" prefix={<KeyOutlined />} />
                </Form.Item>
              )}
              <Form.Item name="newPassword" rules={[{ required: true, message: 'Please enter new password' }]}>
                <Input.Password autoComplete="new-password" placeholder="New Password" prefix={<LockOutlined />} />
              </Form.Item>
              <Form.Item
                name="confirmNewPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}>
                <Input.Password autoComplete="new-password" placeholder="Confirm New Password" prefix={<LockOutlined />} />
              </Form.Item>
            </>
          )}

          <Button
            htmlType="submit"
            size="large"
            block
            loading={loading}
            disabled={ssoLoading}
            style={{ backgroundColor: token.colorPrimary, color: 'white', borderColor: token.colorPrimary, borderRadius: '5px' }}>
            {authView === 'login'
              ? 'Sign in'
              : authView === 'forgot-request'
                ? 'Send Code'
                : authView === 'new-password-required'
                  ? 'Set New Password'
                  : 'Reset Password'}
          </Button>
        </Form>

        {/* Microsoft SSO Button */}
        {ssoEnabled && authView === 'login' && (
          <>
            <Divider plain style={{ margin: '12px 0', fontSize: 12, color: '#999' }}>or</Divider>
            <Button
              size="large"
              block
              onClick={handleSSOLogin}
              loading={ssoLoading}
              disabled={loading}
              icon={<WindowsOutlined />}
              style={{
                backgroundColor: '#2F2F2F',
                color: 'white',
                borderColor: '#2F2F2F',
                borderRadius: '5px',
                fontWeight: 500,
                height: 42,
              }}>
              Sign in with SSO
            </Button>
          </>
        )}

        {authView !== 'login' && (
          <div className="mt-3 text-center">
            <Button type="link" onClick={goToLogin}>
              Back to sign in
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Login;
